'use client';

import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

import { Typography } from '@components/typography';
import { Link } from '@components/link';

// prettier-ignore
const faqs = [
  {
    id: 1,
    question: "How is Savvy different from other vacation rental platforms?",
    answer:
      "Savvy lowers travel costs and gives guests peace of mind. By not charging platform service fees, guests typically save 15-20% over the same property on other sites. This savings allows travelers to book longer stays, more expensive stays, or travel more often. Savvy also exclusively lists properties hosted by professional property management companies, giving guests peace of mind knowing that a professional host is available to ensure a worry-free stay.",
  },
  {
    id: 2,
    question: "What are the benefits of listing on Savvy?",
    answer:
      "Savvy is the first OTA built specifically for hosts by hosts. Savvy empowers hosts with full control over their policies and direct relationships with guests. You own the booking and are the merchant of record, so you can provide hospitality to your guests & potential guests as well as handle any issues efficiently and directly, ensuring clear communication and smooth resolutions. Savvy also helps hosts lower your distribution costs with free listings. Finally, you can control your exposure and placement in the search results with optional Premium upgrade opportunities for low, flat costs.",
  },
  {
    id: 3,
    question: "What rates should I list on Savvy?",
    answer:
      "Your book direct rates. We don't charge transaction fees or traveler booking fees, so your direct pricing is competitive while you keep more margin. You pay 5%  (vs 5-25% on OTAs) per confirmed booking, billed monthly, and you maintain complete control of the booking - your PMS, your data, your guest relationships.\n\nWe only succeed when you get bookings, so our model is built around your success. Your book direct rates work because our fees are lower and you own everything.",
  },
  {
    id: 4,
    question: "How hard is it to onboard and manage my properties on Savvy?",
    answer:
      "Hosts using one of Savvy's partner Property Management Systems (PMS) can onboard in minutes. Simply enter your company details, connect your PMS, and your listings will be live. Most of our hosts say it's the easiest onboarding they have ever done! Listings are then automatically managed directly through your PMS, so no additional updates are needed.",
  },
  {
    id: 5,
    question: "Which property management systems does Savvy support?",
    answer:
      "View our ",
    answerLink: {
      text: "Integrations Page",
      href: "https://www.savvy.com/integrations",
    },
    answerEnd: " to see our list of software partners.",
  },
  {
    id: 6,
    question: "Can I use Savvy alongside other platforms like Airbnb and VRBO?",
    answer:
      "Absolutely. Savvy integrates directly with your PMS, so your availability, rates, and calendar stay synced across all platforms. There's no exclusivity requirement - Savvy is designed to complement your existing distribution strategy while giving you a lower-cost, direct booking channel.",
  },
  {
    id: 7,
    question: "When do I pay the 5% revenue share?",
    answer:
      "Our model is based on helping hosts drive low-cost bookings without mandatory fees. You pay 5% per confirmed booking, billed monthly. Savvy also generates revenue through optional Premium listing upgrades for hosts who want increased visibility.",
  },
  {
    id: 8,
    question: "Can I pause or remove my listings anytime?",
    answer:
      "Yes - you have full control over your listings at all times. You can pause, unpublish, or remove any listing directly through your Savvy dashboard whenever you need to.",
  },
  {
    id: 9,
    question: "What kind of support does Savvy offer?",
    answer:
      "Savvy's team includes industry veterans with a combined 100+ years in online travel and hospitality. We offer dedicated support to help you get set up, optimize your listings, and drive bookings. Reach out anytime through our support portal.",
  },
];

export const FaqsSection: React.FC = () => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-white w-full">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-16 text-primary-main">
        <Typography
          variant="title4"
          component="h2"
          className="text-center !text-3xl sm:!text-4xl lg:!text-5xl mb-10"
        >
          FAQs
        </Typography>

        <div className="flex flex-col gap-3">
          {faqs.map((item, index) => (
            <div
              key={item.id}
              className="bg-off-white rounded-xl overflow-hidden transition-all duration-300"
            >
              <button
                className="w-full py-5 px-6 flex justify-between items-center text-left"
                onClick={() => toggleQuestion(index)}
              >
                <Typography
                  component="h3"
                  variant="body1"
                  className="!font-semibold text-primary-main pr-4"
                >
                  {item.question}
                </Typography>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-primary-main h-4 w-4 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[500px]' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <Typography
                    component="p"
                    variant="body1"
                    className="text-primary-main/80 leading-relaxed whitespace-pre-line"
                  >
                    {item.answer}
                    {item.answerLink && (
                      <>
                        <Link
                          to={item.answerLink.href}
                          className="text-primary-main underline underline-offset-2 hover:text-primary-main/70"
                        >
                          {item.answerLink.text}
                        </Link>
                        {item.answerEnd}
                      </>
                    )}
                  </Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
